import { Icons } from '@social-solutions/component-library';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as S from './Table.styles';

import { IDPOptions } from 'models/identity-provider/identity-provider';
import { v4 as uuid } from 'uuid';
import CopyButton from '../Button/CopyButton';
import Tooltip from '../ReactTooltip/Tooltip';
import ToggleCell from './ToggleCell';
import TableWithCustomRow, { IDataService } from './WithCustomRow';
import { Configuration as IConfiguration } from 'models/schema-generated-models/idp/configuration';

export interface IConfigutationTableProps {
    configurations: IConfiguration[];
    onDeleted(config: IConfiguration): void;
    onToggled(configurationName: string, providerName: string, value: boolean): void;
    onCopied(url: string): void;
}

export interface IConfigutationTableRow {
    isExpanded: boolean;
    original: IConfiguration;
    getToggleRowExpandedProps(): Record<string, unknown>;
}

export default function ConfigurationTable({ configurations, onDeleted, onToggled, onCopied }: IConfigutationTableProps): JSX.Element {

    const { t } = useTranslation();
    const [data, setData] = useState([] as IConfiguration[]);

    useEffect(() => {
        setData(configurations);
    }, [setData, configurations]);

    const serverDataSource = {
        data: data,
        getData() {
            return this.data.map((item: IConfiguration) => {
                return {
                    ...item,
                    id: item.ProviderName
                };
            });
        },
        getPaginatedData(start: number, end: number) {
            return this.getData().slice(start, end);
        },
        getPageCount(pageSize: number) {
            return Math.ceil(Object.entries(this.data).length / pageSize);
        },
        updateData(id: string, _column: string, value: boolean) {
            return this.data
                .filter((item: IConfiguration) => item.ProviderName === id)
                .map((item: IConfiguration)  => item.Status = value);
        }
    } as IDataService;

    const updateToggleData = (data: IConfiguration, _rowKey: string, value: boolean) => {
        serverDataSource.updateData(data.ProviderName, value);
        onToggled(data.ConfigurationName, data.ProviderName, value);
    };

    const copyURL = (url:string) => {
        navigator.clipboard.writeText(url.replace(/['"]+/g, ''));
        onCopied(url);
    };

    const tooltipId = useMemo(() => uuid(), []);

    const columns = [
        {
            Header: () => null,
            accessor: 'ProviderName',
            id: 'expander',
            Cell: ({ row }: { row: IConfigutationTableRow }) => (
                <span {...row.getToggleRowExpandedProps()}>
                    {row.isExpanded ? <Icons.Collapse /> : <Icons.Expand />}
                </span>
            ),
            width: 20
        },
        {
            Header: 'NAME',
            accessor: 'ConfigurationName',
            isSortable: true
        },
        {
            Header: () => null,
            accessor: 'ProviderType',
            Cell: ({ row }: { row: IConfigutationTableRow }) => (
                <span>
                    {row.original.ProviderType === IDPOptions.SAML ? <S.BadgeIcon>SAML</S.BadgeIcon> : <S.BadgeIcon>OIDC</S.BadgeIcon>}
                </span>
            ),
            isSortable: true,
            width: 80
        },
        {
            Header: 'Login URL',
            accessor: 'LoginURL',
            Cell: ({ row }: { row: IConfigutationTableRow }) => (
                <span>
                    {row.original.LoginURL ?
                        <div className='url-wrapper'>
                            <S.URL>{row.original.LoginURL}</S.URL>
                        </div>: <></>}
                </span>
            ),
            isSortable: true,
            width: '50%'
        },
        {
            Header: () => null,
            accessor: 'Copy',
            Cell: ({ row }: { row: IConfigutationTableRow }) => (
                <Tooltip text="Copy" tooltipId={tooltipId}>
                    <CopyButton onClick={() => copyURL(row.original.LoginURL)} data-tip data-for={tooltipId}/>
                </Tooltip>
            ),
            isSortable: false,
            width: 40
        },
        {
            Header: 'Enabled',
            accessor: 'Enabled',
            Cell: ({ row }: { row: IConfigutationTableRow }) => (
                <ToggleCell updateToggleData={updateToggleData} column={{id: row.original.ProviderName}} row={row} />
            ),
            isSortable: true,
        },
        {
            Header: () => null,
            accessor: 'Actions',
            Cell: ({ row }: { row: IConfigutationTableRow }) => (
                <S.StyledKebab items={
                    [{children: (<>
                        <S.KebabItem onClick={() => onDeleted(row.original) }>
                            <S.TrashIcon size="18"/>
                            <span className='kebab-item-button-text'>DELETE CONFIGURATION</span>
                        </S.KebabItem>
                    </>)}]
                } openOffset={[5, -210]} />
            ),
            isSortable: false,
            width: 40
        }
    ];

    const renderRowSubComponent = ({ row }: { row: IConfigutationTableRow }) => {
        const { LoginURL: loginURL } = row.original;
        const DETAIL_LOGIN_APP_URL = row.original.ProviderType === IDPOptions.SAML ? t('SAML.DETAIL_LOGIN_APP_URL') : t('OIDC.DETAIL_LOGIN_APP_URL');

        return (
            <div>
                <S.FormGroup>
                    <S.StyledLabel><Trans>{DETAIL_LOGIN_APP_URL}</Trans></S.StyledLabel>
                    <S.StyledText>{loginURL}</S.StyledText>
                    <S.ActionButton onClick={() => copyURL(loginURL)}><Trans>BUTTON.LABEL_COPY</Trans></S.ActionButton>
                </S.FormGroup>
            </div>
        );
    };

    return (
        <S.TableWrapper>
            <TableWithCustomRow
                className='table-with-custom-row'
                serverData={serverDataSource}
                columns={columns}
                disablePagination={false}
                hidePagination={false}
                pageSize={15}
                initialStates={{
                    pageIndex: 0,
                    pageSize: 10,
                }}
                pageSizeValues={[5, 10, 15, 20]}
                showHeaders={true}
                updateMyData={updateToggleData}
                renderRowSubComponent={renderRowSubComponent}
            />
        </S.TableWrapper>
    );
}
